/**
 * Normalize LLM output for react-markdown + remark-gfm + remark-breaks.
 * Fixes MediaWiki-style table headers (|| cell ||) so GFM tables render.
 */
function normalizeDoublePipeTableRows(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  for (const line of lines) {
    const lead = line.match(/^\s*/)?.[0] ?? '';
    const trimmed = line.trim();
    if (!trimmed) {
      out.push(line);
      continue;
    }
    // Only rows that start with || (LLM / MediaWiki style); avoids splitting "| a || b |" data rows.
    if (/^\|\|/.test(trimmed) && trimmed.includes('||')) {
      const cells = trimmed.split('||').map((c) => c.trim()).filter((c) => c.length > 0);
      if (cells.length >= 2) {
        out.push(`${lead}| ${cells.join(' | ')} |`);
        continue;
      }
    }
    out.push(line);
  }
  return out.join('\n');
}

function isGfmDelimiterRow(t: string): boolean {
  const s = t.trim();
  if (!s.startsWith('|')) return false;
  const parts = s.split('|').filter((p) => p.trim().length > 0);
  return parts.length >= 1 && parts.every((p) => /^[\s:-]+$/.test(p)) && parts.some((p) => /-/.test(p));
}

/** Looks like a GFM table body/header row (| … | … |), not a delimiter row. */
function isGfmTableRow(t: string): boolean {
  const s = t.trim();
  if (!s.startsWith('|') || !s.endsWith('|')) return false;
  if (isGfmDelimiterRow(t)) return false;
  const inner = s.slice(1, -1);
  return inner.includes('|');
}

/** Ensure a blank line before a GFM table so it is not merged into the previous paragraph. */
function ensureBlankLineBeforeTables(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const next = lines[i + 1];
    if (next !== undefined && isGfmTableRow(line) && isGfmDelimiterRow(next)) {
      const prev = out[out.length - 1];
      if (
        prev !== undefined &&
        prev.trim() !== '' &&
        !isGfmTableRow(prev) &&
        !isGfmDelimiterRow(prev)
      ) {
        out.push('');
      }
    }
    out.push(line);
  }
  return out.join('\n');
}

export function normalizeAiMarkdown(content: string): string {
  let s = content.trim();
  if (!s) return s;
  s = normalizeDoublePipeTableRows(s);
  s = ensureBlankLineBeforeTables(s);
  s = s.replace(/([^\n])\n([-*+]\s)/g, '$1\n\n$2');
  s = s.replace(/(\*\*[^*]+\*\*)\n([-*+]\s)/g, '$1\n\n$2');
  s = s.replace(/([^\n])\n(\d+\.\s)/g, '$1\n\n$2');
  s = s.replace(/(\*\*[^*]+\*\*)\n(\d+\.\s)/g, '$1\n\n$2');
  return s;
}
